import { useRef, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLockAccountMutation } from "src/store/features/api/generated";
import {
  useAccountLockState,
  STAGES,
  setCurrentStage,
  toggleAccountLockModal,
} from "./accountLock.slice";

export function useAccountLockForm() {
  const { currentStage } = useAccountLockState();
  const dispatch = useDispatch();

  let title = `Lock your account`;
  if (currentStage === STAGES.COMPLETED) {
    title = `Account locked`;
  }

  /*************
   * STATE *
   *************/
  const [lockReason, setLockReason] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  /*************
   * API CALLS *
   *************/
  const [lockAccount, { isLoading: isLoadingLockAccount, isSuccess: isSuccessLockAccount }] =
    useLockAccountMutation();

  const handleSubmit = async () => {
    if (!lockReason) {
      setErrorMsg("Please enter a reason for locking your account.");
      return;
    }

    try {
      await lockAccount({ lockAccountDto: { lockReason, lockType: "Full" } });
      dispatch(setCurrentStage(STAGES.COMPLETED));
    } catch (error: any) {
      if (error?.message) {
        setErrorMsg(error.message);
      } else {
        setErrorMsg("Something went wrong, please try again.");
      }
    }
  };

  /*************
   * INPUT REF HANDLING *
   *************/
  const lockReasonInputRef = useRef<HTMLInputElement | null>(null);

  const [selection, setSelection] = useState<any>();
  const [selectedInput, setSelectedInput] = useState<React.RefObject<HTMLInputElement> | null>(
    null,
  );

  useEffect(() => {
    if (selection) {
      const { start, end } = selection;
      selectedInput?.current?.focus();
      selectedInput?.current?.setSelectionRange(start, end);
    }
  }, [selection]);

  const setInputSelection = (
    inputRef: React.RefObject<HTMLInputElement>,
    selection: { start: number | null; end: number | null },
  ) => {
    if (inputRef.current && selection.start !== null && selection.end !== null) {
      setSelection({ start: selection.start, end: selection.end });
      setSelectedInput(inputRef);
    }
  };

  /*************
   * HANDLERS *
   *************/
  // INPUT FIELDS
  const handleLockReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart, selectionEnd } = event.target;
    setLockReason(value);
    setInputSelection(lockReasonInputRef, { start: selectionStart, end: selectionEnd });
  };

  const handleClose = () => {
    // if any of the API calls are loading, do not close the modal
    if (isLoadingLockAccount) return;

    dispatch(toggleAccountLockModal()); // close modal
  };

  const resetInputForm = () => {
    setLockReason("");
    setErrorMsg("");
  };

  const handleAcknowledgeClick = async () => {
    dispatch(setCurrentStage(STAGES.FORM));
    resetInputForm();
  };

  return {
    handleClose,
    title,
    handleAcknowledgeClick,
    lockReason,
    errorMsg,
    handleLockReasonChange,
    isLoadingLockAccount,
    isSuccessLockAccount,
    handleSubmit,
    lockReasonInputRef,
  };
}
